import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { callGetDocumentDownloadUrl } from "@documents/store/thunks/callGetDocmentDownloadUrlApi";
import { openNotificationThunk } from "@store/Notification/thunks";
import { ViewDocumentArgs } from "@documents/models/api.models";
import { checkDocumentTerms } from "@terms/store/thunks/checkDocumentTerms";
import {
  closeDocumentDialog,
  openDocumentDialog,
  setDocumentDialogError,
  setDocumentDialogUrl,
} from "@pdfViewer/store/ducks";
import { createInFlightKeySet } from "@shared/utils/inFlightRegistry";

const FAILED_TO_LOAD_PDF_MESSAGE = "Failed to load PDF. Please try again later.";
const GENERIC_VIEW_ERROR_MESSAGE = "Something went wrong. Please try again";

const inFlightDocumentViews = createInFlightKeySet<string>();

export const viewDocument = createAsyncThunk<void, ViewDocumentArgs, ThunkApiConfig>(
  "documents/viewDocument",
  async (
    { id, markedRead, documentSubtype, fundId, readOnlyFlag, title, onViewUnreadDocument },
    { dispatch, rejectWithValue }
  ) => {
    const documentKey = String(id);

    if (inFlightDocumentViews.has(documentKey)) {
      return;
    }

    inFlightDocumentViews.add(documentKey);

    let didOpenDialog = false;

    try {
      const hasAcceptedTerms = await dispatch(
        checkDocumentTerms({
          documentSubTypes: [String(documentSubtype)],
          fundIds: [fundId],
        })
      );

      if (!hasAcceptedTerms) {
        return;
      }

      if (readOnlyFlag) {
        dispatch(
          openDocumentDialog({
            documentName: title,
            documentId: id,
            documentUrl: null,
          })
        );
        didOpenDialog = true;
      }

      const downloadUrl = await dispatch(callGetDocumentDownloadUrl(id!));

      if (!downloadUrl) {
        if (readOnlyFlag) {
          dispatch(
            setDocumentDialogError({
              documentId: id,
              error: FAILED_TO_LOAD_PDF_MESSAGE,
            })
          );
        } else {
          dispatch(
            openNotificationThunk({
              variant: "warning",
              text: GENERIC_VIEW_ERROR_MESSAGE,
            })
          );
        }

        return rejectWithValue("Empty download URL");
      }

      if (!markedRead) {
        dispatch(onViewUnreadDocument([id!]));
      }

      if (readOnlyFlag) {
        dispatch(
          setDocumentDialogUrl({
            documentId: id,
            documentUrl: downloadUrl,
          })
        );
      } else {
        window.open(downloadUrl, "_blank")?.focus();
      }
    } catch (e) {
      if (didOpenDialog) {
        dispatch(closeDocumentDialog());
      }

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: GENERIC_VIEW_ERROR_MESSAGE,
        })
      );

      return rejectWithValue("Failed to view the document");
    } finally {
      inFlightDocumentViews.delete(documentKey);
    }
  }
);
